/**
 * County- and city-level GHG + population for Analysis map (per-capita choropleth).
 * Joins ghg_inventory_precalculated with population for the same year.
 */

import { inventoryGeofipsNumeric } from './ghgInventoryGeofipsCanonical';
import { supabaseClient } from './supabase';

const PAGE_SIZE = 1000;

export type CountyGhgMapMetric =
  | 'total'
  | 'transport_ldv_hdv'
  | 'ldv'
  | 'hdv'
  | 'electricity'
  | 'natural_gas'
  | 'agriculture'
  | 'offroad'
  | 'waste';

export const COUNTY_GHG_MAP_METRIC_OPTIONS: { value: CountyGhgMapMetric; label: string }[] = [
  { value: 'total', label: 'Total (all sectors)' },
  { value: 'transport_ldv_hdv', label: 'Transportation (LDV + HDV)' },
  { value: 'ldv', label: 'Light-duty vehicles' },
  { value: 'hdv', label: 'Heavy-duty vehicles' },
  { value: 'electricity', label: 'Electricity (res. + com.)' },
  { value: 'natural_gas', label: 'Natural gas (res. + com.)' },
  { value: 'agriculture', label: 'Agriculture' },
  { value: 'offroad', label: 'Off-road' },
  { value: 'waste', label: 'Waste' },
];

export interface CountyGhgMapRow {
  geofips: number;
  /** Census COUNTYFP (3 digits, e.g. "037") for tract GeoJSON join */
  countyFp3: string;
  location_name: string;
  population: number;
  total_mt: number;
  ldv_mt: number;
  hdv_mt: number;
  elec_mt: number;
  ng_mt: number;
  ag_mt: number;
  offroad_mt: number;
  waste_mt: number;
}

export interface CountyGhgMapPayload {
  year: number;
  rows: CountyGhgMapRow[];
  error: string | null;
}

function num(v: unknown): number {
  if (v == null) return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

/**
 * For map display only: `ghg_inventory_precalculated.waste_emissions` for 2005 is often 0 because
 * `total_waste` / landfill coverage frequently has no usable 2005 row, so the populate script leaves
 * tons null and writes zero emissions. When 2005 waste is zero but 2006 has positive waste, use 2006
 * so the Waste choropleth and quintiles behave like other years.
 */
async function fetchWasteMtByGeofips(year: number, geographyType: 'County' | 'City'): Promise<Map<number, number>> {
  const out = new Map<number, number>();
  let from = 0;
  const isCounty = geographyType === 'County';
  for (;;) {
    let q = supabaseClient
      .from('ghg_inventory_precalculated')
      .select('geofips,waste_emissions')
      .eq('geography_type', geographyType)
      .eq('year', year);
    q = isCounty
      ? q.gte('geofips', 6000).lt('geofips', 7000)
      : q.gte('geofips', 600000).lt('geofips', 700000);
    const { data, error } = await q.order('geofips', { ascending: true }).range(from, from + PAGE_SIZE - 1);

    if (error) break;
    const batch = (data ?? []) as Record<string, unknown>[];
    if (batch.length === 0) break;
    for (const r of batch) {
      const raw = r.geofips;
      const gf = isCounty ? Number(raw) : inventoryGeofipsNumeric(raw);
      if (gf == null || !Number.isFinite(gf)) continue;
      const w = num(r.waste_emissions);
      if (w > 0) out.set(gf, w);
    }
    if (batch.length < PAGE_SIZE) break;
    from += PAGE_SIZE;
  }
  return out;
}

function applyWaste2005MapFallback(rows: CountyGhgMapRow[], waste2006: Map<number, number>): CountyGhgMapRow[] {
  return rows.map((row) => {
    const w6 = waste2006.get(row.geofips);
    if (row.waste_mt === 0 && w6 != null && w6 > 0) {
      return { ...row, waste_mt: w6 };
    }
    return row;
  });
}

function countyFpFromGeofips(geofips: number): string {
  const n = geofips % 1000;
  return String(n).padStart(3, '0');
}

/** Sector total (MT CO₂e) for the chosen map metric. */
export function sectorMtForMetric(row: CountyGhgMapRow, metric: CountyGhgMapMetric): number {
  switch (metric) {
    case 'total':
      return row.total_mt;
    case 'transport_ldv_hdv':
      return row.ldv_mt + row.hdv_mt;
    case 'ldv':
      return row.ldv_mt;
    case 'hdv':
      return row.hdv_mt;
    case 'electricity':
      return row.elec_mt;
    case 'natural_gas':
      return row.ng_mt;
    case 'agriculture':
      return row.ag_mt;
    case 'offroad':
      return row.offroad_mt;
    case 'waste':
      return row.waste_mt;
    default:
      return row.total_mt;
  }
}

/** Metric tons CO₂e per person (inventory uses MT). */
export function perCapitaForMetric(row: CountyGhgMapRow, metric: CountyGhgMapMetric): number | null {
  if (row.population <= 0) return null;
  const mt = sectorMtForMetric(row, metric);
  if (!Number.isFinite(mt)) return null;
  return mt / row.population;
}

export type CountyGhgMapDisplayMode = 'per_capita' | 'total_emissions';

/**
 * Positive numeric value for map quintile coloring: per capita (t CO₂e / person) or sector total (MT CO₂e).
 */
export function choroplethValueForMap(
  row: CountyGhgMapRow,
  metric: CountyGhgMapMetric,
  mode: CountyGhgMapDisplayMode
): number | null {
  if (mode === 'per_capita') {
    return perCapitaForMetric(row, metric);
  }
  const mt = sectorMtForMetric(row, metric);
  if (!Number.isFinite(mt) || mt <= 0) return null;
  return mt;
}

export async function fetchCaliforniaCountyGhgForMap(year: number): Promise<CountyGhgMapPayload> {
  type GhgRow = {
    geofips: number;
    location_name: string;
    total_emissions: unknown;
    ldv_emissions: unknown;
    hdv_emissions: unknown;
    elec_total_emissions: unknown;
    ng_total_emissions: unknown;
    waste_emissions: unknown;
    offroad_emissions: unknown;
    agriculture_emissions: unknown;
  };

  const rawGhg: GhgRow[] = [];
  let from = 0;
  for (;;) {
    const { data, error } = await supabaseClient
      .from('ghg_inventory_precalculated')
      .select(
        'geofips,location_name,total_emissions,ldv_emissions,hdv_emissions,elec_total_emissions,ng_total_emissions,waste_emissions,offroad_emissions,agriculture_emissions'
      )
      .eq('geography_type', 'County')
      .eq('year', year)
      .gte('geofips', 6000)
      .lt('geofips', 7000)
      .order('geofips', { ascending: true })
      .range(from, from + PAGE_SIZE - 1);

    if (error) {
      return { year, rows: [], error: error.message };
    }
    const batch = (data ?? []) as Record<string, unknown>[];
    if (batch.length === 0) break;
    for (const r of batch) {
      const gf = Number(r.geofips);
      if (!Number.isFinite(gf)) continue;
      rawGhg.push({
        geofips: gf,
        location_name: String(r.location_name || '').trim(),
        total_emissions: r.total_emissions,
        ldv_emissions: r.ldv_emissions,
        hdv_emissions: r.hdv_emissions,
        elec_total_emissions: r.elec_total_emissions,
        ng_total_emissions: r.ng_total_emissions,
        waste_emissions: r.waste_emissions,
        offroad_emissions: r.offroad_emissions,
        agriculture_emissions: r.agriculture_emissions,
      });
    }
    if (batch.length < PAGE_SIZE) break;
    from += PAGE_SIZE;
  }

  const byGeofips = new Map<number, GhgRow>();
  for (const r of rawGhg) {
    const name = r.location_name.toLowerCase();
    if (!r.location_name || name === 'california') continue;
    byGeofips.set(r.geofips, r);
  }

  const popByGeofips = new Map<number, number>();
  from = 0;
  for (;;) {
    const { data, error } = await supabaseClient
      .from('population')
      .select('geofips,population')
      .eq('geography_type', 'County')
      .eq('year', year)
      .gte('geofips', 6000)
      .lt('geofips', 7000)
      .range(from, from + PAGE_SIZE - 1);

    if (error) {
      return { year, rows: [], error: error.message };
    }
    const batch = (data ?? []) as Record<string, unknown>[];
    if (batch.length === 0) break;
    for (const r of batch) {
      const gf = Number(r.geofips);
      const p = num(r.population);
      if (Number.isFinite(gf) && p > 0) {
        popByGeofips.set(gf, p);
      }
    }
    if (batch.length < PAGE_SIZE) break;
    from += PAGE_SIZE;
  }

  const rows: CountyGhgMapRow[] = [];
  for (const g of Array.from(byGeofips.values())) {
    rows.push({
      geofips: g.geofips,
      countyFp3: countyFpFromGeofips(g.geofips),
      location_name: g.location_name,
      population: popByGeofips.get(g.geofips) ?? 0,
      total_mt: num(g.total_emissions),
      ldv_mt: num(g.ldv_emissions),
      hdv_mt: num(g.hdv_emissions),
      elec_mt: num(g.elec_total_emissions),
      ng_mt: num(g.ng_total_emissions),
      ag_mt: num(g.agriculture_emissions),
      offroad_mt: num(g.offroad_emissions),
      waste_mt: num(g.waste_emissions),
    });
  }

  rows.sort((a, b) => a.geofips - b.geofips);
  return { year, rows, error: null };
}

export interface CountyCityGhgMapPayload {
  year: number;
  countyRows: CountyGhgMapRow[];
  cityRows: CountyGhgMapRow[];
  error: string | null;
}

/** California cities (geofips 6xxxxx) for the same per-capita map metrics as counties. */
export async function fetchCaliforniaCityGhgForMap(year: number): Promise<CountyGhgMapPayload> {
  type GhgRow = {
    geofips: unknown;
    location_name: string;
    total_emissions: unknown;
    ldv_emissions: unknown;
    hdv_emissions: unknown;
    elec_total_emissions: unknown;
    ng_total_emissions: unknown;
    waste_emissions: unknown;
    offroad_emissions: unknown;
    agriculture_emissions: unknown;
  };

  const rawGhg: GhgRow[] = [];
  let from = 0;
  for (;;) {
    const { data, error } = await supabaseClient
      .from('ghg_inventory_precalculated')
      .select(
        'geofips,location_name,total_emissions,ldv_emissions,hdv_emissions,elec_total_emissions,ng_total_emissions,waste_emissions,offroad_emissions,agriculture_emissions'
      )
      .eq('geography_type', 'City')
      .eq('year', year)
      .gte('geofips', 600000)
      .lt('geofips', 700000)
      .order('geofips', { ascending: true })
      .range(from, from + PAGE_SIZE - 1);

    if (error) {
      return { year, rows: [], error: error.message };
    }
    const batch = (data ?? []) as Record<string, unknown>[];
    if (batch.length === 0) break;
    for (const r of batch) {
      rawGhg.push({
        geofips: r.geofips,
        location_name: String(r.location_name || '').trim(),
        total_emissions: r.total_emissions,
        ldv_emissions: r.ldv_emissions,
        hdv_emissions: r.hdv_emissions,
        elec_total_emissions: r.elec_total_emissions,
        ng_total_emissions: r.ng_total_emissions,
        waste_emissions: r.waste_emissions,
        offroad_emissions: r.offroad_emissions,
        agriculture_emissions: r.agriculture_emissions,
      });
    }
    if (batch.length < PAGE_SIZE) break;
    from += PAGE_SIZE;
  }

  const byGeofips = new Map<number, GhgRow>();
  for (const r of rawGhg) {
    const name = r.location_name.toLowerCase();
    if (!r.location_name || name === 'california') continue;
    const gf = inventoryGeofipsNumeric(r.geofips);
    if (gf == null) continue;
    byGeofips.set(gf, r);
  }

  const popByGeofips = new Map<number, number>();
  from = 0;
  for (;;) {
    const { data, error } = await supabaseClient
      .from('population')
      .select('geofips,population')
      .eq('geography_type', 'City')
      .eq('year', year)
      .gte('geofips', 600000)
      .lt('geofips', 700000)
      .range(from, from + PAGE_SIZE - 1);

    if (error) {
      return { year, rows: [], error: error.message };
    }
    const batch = (data ?? []) as Record<string, unknown>[];
    if (batch.length === 0) break;
    for (const r of batch) {
      const gf = inventoryGeofipsNumeric(r.geofips);
      const p = num(r.population);
      if (gf != null && p > 0) {
        popByGeofips.set(gf, p);
      }
    }
    if (batch.length < PAGE_SIZE) break;
    from += PAGE_SIZE;
  }

  const rows: CountyGhgMapRow[] = [];
  for (const g of Array.from(byGeofips.values())) {
    const gf = inventoryGeofipsNumeric(g.geofips);
    if (gf == null) continue;
    rows.push({
      geofips: gf,
      countyFp3: '',
      location_name: g.location_name,
      population: popByGeofips.get(gf) ?? 0,
      total_mt: num(g.total_emissions),
      ldv_mt: num(g.ldv_emissions),
      hdv_mt: num(g.hdv_emissions),
      elec_mt: num(g.elec_total_emissions),
      ng_mt: num(g.ng_total_emissions),
      ag_mt: num(g.agriculture_emissions),
      offroad_mt: num(g.offroad_emissions),
      waste_mt: num(g.waste_emissions),
    });
  }

  rows.sort((a, b) => a.geofips - b.geofips);
  return { year, rows, error: null };
}

export async function fetchCaliforniaCountyCityGhgForMap(year: number): Promise<CountyCityGhgMapPayload> {
  const [c, ct] = await Promise.all([fetchCaliforniaCountyGhgForMap(year), fetchCaliforniaCityGhgForMap(year)]);
  const err = c.error ?? ct.error;
  let countyRows = c.rows;
  let cityRows = ct.rows;
  if (year === 2005 && !err) {
    const [wCounty, wCity] = await Promise.all([
      fetchWasteMtByGeofips(2006, 'County'),
      fetchWasteMtByGeofips(2006, 'City'),
    ]);
    countyRows = applyWaste2005MapFallback(countyRows, wCounty);
    cityRows = applyWaste2005MapFallback(cityRows, wCity);
  }
  return {
    year,
    countyRows,
    cityRows,
    error: err,
  };
}
